import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useMerchantPayoutFlags } from '../../hooks/useMerchantPayoutFlags';
import LogoutModal from '../../components/LogoutModal';
import WalletCreditsModal from '../../components/WalletCreditsModal';
import TransactionSlip from '../../components/TransactionSlip';
import AdminPayoutStatusModal from '../../components/AdminPayoutStatusModal';
import ResponseMessageModal from '../../components/ResponseMessageModal';
import Footer from '../../components/Footer';
interface PayoutTransaction {
  id: string;
  createdAt: string;
  createdAtRaw: string; // Raw ISO date string for proper parsing
  paymentType: string;
  amount: number;
  currentWalletBalance: number;
  status: 'Success' | 'Pending' | 'Failed';
  errorMessage?: string;
  /** Full response_data from payout (object or JSON string) for Response Message modal */
  responseData?: string | object | null;
  contact: string;
  createdBy: string;
  transactionId: string;
  merchantReference: string;
  bankReference: string;
  accountNumber: string;
  ifscCode: string;
  transactionFees: number;
  source?: string;
}

interface PayoutFilters {
  fromDate: string;
  toDate: string;
  paymentType: string;
  transactionId: string;
  status: string;
  accountNumber: string;
  bankReference: string;
  payoutId: string;
  amount: string;
}

const MerchantPayouts: React.FC = () => {
  const { user, logout } = useAuth();
  const { viyonaEnabled, poolpayEnabled } = useMerchantPayoutFlags();
  const [transactions, setTransactions] = useState<PayoutTransaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<PayoutTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('payouts');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<PayoutTransaction | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusData, setStatusData] = useState<any>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showResponseMessageModal, setShowResponseMessageModal] = useState(false);
  const [responseMessageData, setResponseMessageData] = useState<string | object | null>(null);
  const [filters, setFilters] = useState<PayoutFilters>({
    fromDate: '',
    toDate: '',
    paymentType: '',
    transactionId: '',
    status: '',
    accountNumber: '',
    bankReference: '',
    payoutId: '',
    amount: ''
  });

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    // Only fetch data on initial mount
    fetchPayoutData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  const formatTransactionType = (transactionType: string) => {
    switch (transactionType) {
      case 'wallet_credit':
        return 'Wallet Credit';
      case 'wallet_debit':
        return 'Wallet Debit';
      case 'escrow_hold':
        return 'Escrow Hold';
      case 'escrow_release':
        return 'Escrow Release';
      case 'payout_debit':
        return 'Payout Debit';
      case 'payout_credit':
        return 'Payout Credit';
      default:
        // Convert snake_case to Title Case as fallback
        return transactionType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    }
  };

  const fetchPayoutData = async (searchFilters?: PayoutFilters, pageNumber?: number) => {
    try {
      //setLoading(true); 
      
      const filters = searchFilters || {
        fromDate: '',
        toDate: '',
        paymentType: '',
        transactionId: '',
        status: '',
        accountNumber: '',
        bankReference: '',
        payoutId: '',
        amount: ''
      }; 

      console.log('Filters:', filters); 
      
      // Fetch real payout data from API with pagination and filters
      const apiParams: Record<string, string | number | undefined> = {
        page: pageNumber || currentPage,
        limit: 20,
        from_date: filters.fromDate,
        to_date: filters.toDate,
        status: filters.status,
        account_number: filters.accountNumber,
        bank_reference: filters.bankReference,
        merchant_reference: filters.payoutId,
        transaction_id: filters.transactionId,
        amount: filters.amount
      };
      if (filters.paymentType === 'Payout Credit') {
        apiParams.payment_type = 'credit';
      } else if (filters.paymentType === 'Payout Debit') {
        apiParams.payment_type = 'debit';
      }
      
      // API parameters being sent to backend
      
      const response = await api.get('/merchants/my-payouts', {
        params: apiParams
      });
      const apiData = response.data.data;
      
      // Transform API data to match our component interface
      // Calculate running balance for each transaction (newest first, so we work backwards)
      const currentBalance = apiData.current_wallet_balance || 0;
      let runningBalance = currentBalance;
      
      const transformedTransactions: PayoutTransaction[] = (apiData.payouts || []).map((payout: any, index: number) => {
        // For payouts (debits), add the total amount back to get the balance before this transaction
        // Since payouts reduce the balance, to get the balance before payout, we add back the total debited amount
        const balanceAfterThisTransaction = runningBalance;
        const totalDebited = parseFloat(payout.amount) + parseFloat(payout.transaction_fee_amount || 0);
        runningBalance += totalDebited; // Add back the total amount debited from wallet

        const isCredit = payout.transaction_type_override === 'payout_credit';
        const result: PayoutTransaction = { 
          id: payout.id.toString(),
          createdAt: new Date(payout.created_at).toLocaleString('en-US', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short', 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          createdAtRaw: payout.created_at,
          paymentType: isCredit ? formatTransactionType('payout_credit') : formatTransactionType('payout_debit'),
          amount: parseFloat(payout.amount),
          transactionFees: parseFloat(payout.transaction_fee_amount || 0),
          currentWalletBalance: balanceAfterThisTransaction, // Balance after this transaction
          status: payout.status === 'SUCCESS' ? 'Success' as 'Success' : 
                  payout.status === 'PENDING' || payout.status === 'PROCESSING' ? 'Pending' as 'Pending' : 
                  'Failed' as 'Failed',
          contact: payout.account_name || 'N/A',
          errorMessage: payout.error_message || '',
          responseData: payout.response_data ?? null,
          createdBy: user?.name || 'Merchant',
          transactionId: payout.viyona_transaction_id || payout.merchant_reference_number || 'N/A',
          merchantReference: payout.merchant_reference_number || 'N/A',
          bankReference: payout.bank_reference_number || 'N/A',
          accountNumber: payout.account_number || 'N/A',
          ifscCode: payout.ifsc_code || 'N/A',
          source: payout.payout_source || 'Viyona'
        };
        
        return result; 


      });

      setTransactions(transformedTransactions);
      setFilteredTransactions(transformedTransactions);
      
      // Debug: Log account numbers to see what data we have
      // API response processed successfully
      
      // Update pagination info
      if (apiData.pagination) {
        setTotalPages(apiData.pagination.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching payout data:', error);
      // Show empty state on error
      setTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const merchantSidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'account', label: 'My Account', icon: '🏦' },
    { id: 'payouts', label: 'Payouts', icon: '📋' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    ...(viyonaEnabled ? [{ id: 'create-payout' as const, label: 'Create Payout (v1 Viyona)', icon: '➕' }] : []),
    ...(poolpayEnabled ? [{ id: 'create-payout-v2' as const, label: 'Create Payout (v2 PoolPay)', icon: '💠' }] : []),
    { id: 'logout', label: 'Logout', icon: '🚪' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'home') {
      window.location.href = '/merchant';
    } else if (tabId === 'account') {
      window.location.href = '/merchant/account';
    } else if (tabId === 'reports') {
      window.location.href = '/merchant/reports';
    } else if (tabId === 'create-payout') {
      window.location.href = '/merchant/new-payouts';
    } else if (tabId === 'create-payout-v2') {
      window.location.href = '/merchant/new-payouts/poolpay';
    } else if (tabId === 'logout') {
      setShowLogoutModal(true);
    }
    // For 'payouts', stay on current page (/merchant/payouts)
    // Close sidebar on mobile after navigation
    setSidebarOpen(false);
  };

  const handleCreditMoney = () => {
    setShowCreditsModal(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleAddPayout = () => {
    // Add Payout clicked
    window.location.href = '/merchant/new-payouts';
  };

  const handleMyProfile = () => {
    window.location.href = '/merchant/account';
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault(); // Prevent form submission and page reload
      e.stopPropagation(); // Stop event bubbling
    }
    
    setCurrentPage(1); // Reset to page 1 when searching
    
    // Fetch data with filters from backend instead of client-side filtering
    fetchPayoutData(filters);
  };

  const handleQuickFilter = (filterType: string) => {
    const today = new Date();
    let fromDate = new Date();

    switch (filterType) {
      case 'today':
        fromDate = new Date(today.setHours(0, 0, 0, 0));
        break;
      case 'week':
        fromDate = new Date(today.setDate(today.getDate() - 7));
        break;
      case 'month':
        fromDate = new Date(today.setMonth(today.getMonth() - 1));
        break;
    }

    const newFilters = {
      ...filters,
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0]
    };
    
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1
    fetchPayoutData(newFilters); // Trigger search with new date filters
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      fromDate: '',
      toDate: '',
      paymentType: '',
      transactionId: '',
      status: '',
      accountNumber: '',
      bankReference: '',
      payoutId: '',
      amount: ''
    };
    setCurrentPage(1); // Reset to page 1 when clearing filters
    setFilters(clearedFilters);
    // Fetch data without filters
    fetchPayoutData(clearedFilters);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      // Fetch data with current filters and new page number
      fetchPayoutData(filters, page);
    }
  };


  const handleViewSlip = (transaction: PayoutTransaction) => {
    setSelectedTransaction(transaction);
    setShowSlipModal(true);
  };

  const handleCheckStatus = async (transaction: PayoutTransaction) => {
    try {
      setStatusLoading(true);
      setShowStatusModal(true);
      setStatusData(null);

      // For refund credits (REFUND-PAYOUT_xxx), use the original payout ref (PAYOUT_xxx) for status lookup
      const ref = (transaction.merchantReference || '').trim();
      const statusRef = ref.startsWith('REFUND-') ? ref.slice(7) : ref;

      const isPoolPay = (transaction.source || '').toUpperCase() === 'POOLPAY';
      const response = isPoolPay
        ? await api.get(`/v2/poolpay/merchants/status/${statusRef}`)
        : await api.get(`/merchants/payout-status/${statusRef}`);
      
      if (response.data.success) {
        if (isPoolPay) {
          setStatusData({ poolpay: response.data.data });
        } else {
          setStatusData({ data: response.data.data });
        }
      } else {
        setStatusData({ error: response.data.message || 'Failed to fetch status' });
      }
    } catch (error: any) {
      console.error('Error fetching payout status:', error);
      setStatusData({ 
        error: error.response?.data?.message || error.message || 'Error fetching payout status' 
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDownloadSlip = async () => {
    if (!selectedTransaction) {
      console.error('No transaction selected for slip download.');
      return;
    }

    try {
      const width = 900;
      const height = 1200;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Canvas 2D context not available.');
        return;
      }

      // Try to load logo
      let logoImg: HTMLImageElement | null = null;
      try {
        const img = new Image();
        img.src = `${process.env.PUBLIC_URL || ''}/logo2.png`;
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Logo failed to load'));
        });
        logoImg = img;
      } catch (e) {
        console.warn('Could not load logo for slip, continuing without it.', e);
      }

      // Background (light grey, like page behind card)
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, width, height);

      const margin = 80;
      const cardX = margin;
      const cardY = margin;
      const cardWidth = width - margin * 2;
      const cardHeight = height - margin * 2;
      const radius = 32;

      const drawRoundedRect = (x: number, y: number, w: number, h: number, r: number) => {
        const rr = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        ctx.moveTo(x + rr, y);
        ctx.lineTo(x + w - rr, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
        ctx.lineTo(x + w, y + h - rr);
        ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
        ctx.lineTo(x + rr, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
        ctx.lineTo(x, y + rr);
        ctx.quadraticCurveTo(x, y, x + rr, y);
        ctx.closePath();
      };

      // Card background
      drawRoundedRect(cardX, cardY, cardWidth, cardHeight, radius);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#e5e7eb';
      ctx.stroke();

      let y = cardY + 50;

      // Draw logo centered above title if available
      if (logoImg) {
        const maxLogoWidth = 180;
        const scale = Math.min(maxLogoWidth / logoImg.width, 1);
        const logoWidth = logoImg.width * scale;
        const logoHeight = logoImg.height * scale;
        const logoX = cardX + (cardWidth - logoWidth) / 2;
        ctx.drawImage(logoImg, logoX, y, logoWidth, logoHeight);
        y += logoHeight + 24;
      } else {
        y += 24;
      }

      // Title
      ctx.fillStyle = '#111827';
      ctx.font = 'bold 28px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Transaction Receipt', width / 2, y);
      y += 28;
      ctx.font = '16px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillStyle = '#6b7280';
      ctx.fillText('Payment Confirmation', width / 2, y);

      ctx.textAlign = 'left';
      y += 48;

      const lineGap = 32;
      const labelX = cardX + 48;
      const valueX = labelX + 260;

      const drawRow = (label: string, value: string) => {
        ctx.fillStyle = '#6b7280';
        ctx.font = '14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.fillText(label, labelX, y);
        // Status colors to match UI: Success=green, Refunded=sky, others=default
        if (label.startsWith('Status')) {
          const v = (value || '').toLowerCase();
          if (v === 'success') ctx.fillStyle = '#059669';
          else if (v === 'refunded') ctx.fillStyle = '#0284c7';
          else ctx.fillStyle = '#111827';
        } else {
          ctx.fillStyle = '#111827';
        }
        ctx.font = '15px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        ctx.fillText(value || 'N/A', valueX, y);
        y += lineGap;
        // Row bottom divider line (like table rows)
        ctx.beginPath();
        ctx.moveTo(labelX, y - lineGap + 10);
        ctx.lineTo(cardX + cardWidth - 48, y - lineGap + 10);
        ctx.strokeStyle = '#e5e7eb';
        ctx.stroke();
      };

      drawRow('Merchant Ref:', selectedTransaction.merchantReference || 'N/A');
      drawRow('Date & Time:', selectedTransaction.createdAt || 'N/A');
      drawRow('Beneficiary Name:', selectedTransaction.contact || 'N/A');
      drawRow('Account Number:', selectedTransaction.accountNumber || 'N/A');
      drawRow('IFSC Code:', selectedTransaction.ifscCode || 'N/A');
      drawRow('Bank Reference Number:', selectedTransaction.bankReference || 'N/A');
      drawRow('Payment Mode:', 'IMPS');
      drawRow('Transfer Type:', 'INSTANT');
      const slipStatus = (selectedTransaction.paymentType === 'Payout Credit' && /refund for failed/i.test(selectedTransaction.contact || '')) ? 'Refunded' : selectedTransaction.status;
      drawRow('Status:', slipStatus);

      y += 6;
      ctx.beginPath();
      ctx.moveTo(labelX, y);
      ctx.lineTo(cardX + cardWidth - 48, y);
      ctx.strokeStyle = '#e5e7eb';
      ctx.stroke();
      y += lineGap;

      ctx.fillStyle = '#374151';
      ctx.font = 'bold 18px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillText('Transaction Amount:', labelX, y);
      ctx.fillStyle = '#111827';
      ctx.font = 'bold 22px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillText(
        new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedTransaction.amount),
        valueX,
        y
      );

      // Footer: downloaded timestamp at bottom center
      const footerY = cardY + cardHeight - 32;
      const downloadedAt = new Date().toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      ctx.textAlign = 'center';
      ctx.fillStyle = '#9ca3af';
      ctx.font = '12px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillText(`Downloaded on ${downloadedAt}`, width / 2, footerY);
      ctx.textAlign = 'left';

      const mimeType = 'image/png';
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const transactionId = selectedTransaction.merchantReference || selectedTransaction.id || 'slip';
      const fileName = `transaction-slip-${transactionId}-${timestamp}.png`;

      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Canvas toBlob returned null for slip image.');
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, mimeType);
    } catch (error) {
      console.error('Error generating slip image:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-100 text-violet-600">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
        <p className="text-violet-600 text-base m-0">Loading Payouts...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-100 to-slate-200">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} aria-hidden />}
      <aside
        id="merchant-sidebar"
        className="fixed top-0 left-0 z-50 h-full w-64 bg-[#373a63] text-slate-100 shadow-xl transition-transform duration-200 ease-out"
        style={isDesktop ? { transform: 'translateX(0)' } : sidebarOpen ? { transform: 'translateX(0)' } : { transform: 'translateX(-100%)' }}
      >
        <div className="p-5 border-b border-slate-600">
          <div className="flex items-center gap-2">
            <span className="text-2xl">💳</span>
            <span className="font-bold text-lg">YuvanAPI</span>
          </div>
        </div>
        <nav className="p-4 flex flex-col gap-1">
          {merchantSidebarItems.map(item => (
            <button key={item.id} type="button" className={`flex items-center gap-3 w-full py-3 px-4 rounded-lg text-left font-medium transition-colors ${activeTab === item.id ? 'bg-violet-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`} onClick={() => handleNavigation(item.id)}>
              <span className="text-xl">{item.icon}</span>
              <span className="leading-tight">
                {item.id === 'create-payout' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v1 Viyona)</span>
                  </>
                ) : item.id === 'create-payout-v2' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v2 PoolPay)</span>
                  </>
                ) : (
                  <span className="block">{item.label}</span>
                )}
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <header className="flex items-center gap-4 p-4 bg-[#373a63] text-white shadow sticky top-0 z-30" style={isDesktop ? { display: 'none' } : undefined}>
        <button type="button" className="p-2 rounded-lg hover:bg-slate-700" onClick={toggleSidebar} aria-label="Menu">☰</button>
        <h1 className="text-lg font-bold m-0">YuvanAPI</h1>
      </header>

      <main id="merchant-main" className="flex-1 p-6 pt-8" style={isDesktop ? { marginLeft: '16rem' } : undefined}>
        <div
          className="flex flex-col gap-4 mb-6"
          style={isDesktop ? { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' } : undefined}
        >
          <h1 className="text-2xl font-bold text-slate-800 m-0 shrink-0">Welcome to YuvanAPI Merchant!</h1>
          <div className="flex flex-wrap gap-2" style={{ flexShrink: 0 }}>
            <button type="button" onClick={handleCreditMoney} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-blue-700 whitespace-nowrap shrink-0">💳 CREDIT MONEY</button>
            <button type="button" onClick={handleAddPayout} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 whitespace-nowrap shrink-0">💸 + Payout</button>
            <button type="button" onClick={handleMyProfile} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-slate-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-slate-700 whitespace-nowrap shrink-0">👤 My Profile</button>
            <button type="button" onClick={handleLogoutClick} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-red-700 whitespace-nowrap shrink-0">🚪 Logout</button>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800 m-0 mb-4">Payouts</h2>

          <form onSubmit={(e) => { e.preventDefault(); handleSearch(e); }} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">From Date</label>
                <input type="date" value={filters.fromDate} onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">To Date</label>
                <input type="date" value={filters.toDate} onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Payment Type</label>
                <select value={filters.paymentType} onChange={(e) => setFilters(prev => ({ ...prev, paymentType: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20">
                  <option value="">Select Type</option>
                  <option value="Payout Debit">Payout Debit</option>
                  <option value="Payout Credit">Payout Credit</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Transaction Id</label>
                <input type="text" placeholder="Transaction Id" value={filters.transactionId} onChange={(e) => setFilters(prev => ({ ...prev, transactionId: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Status</label>
                <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20">
                  <option value="">Select Status</option>
                  <option value="success">Success</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Account Number</label>
                <input type="text" placeholder="Account number" value={filters.accountNumber} onChange={(e) => setFilters(prev => ({ ...prev, accountNumber: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Bank Ref / UTR</label>
                <input type="text" placeholder="Bank reference" value={filters.bankReference} onChange={(e) => setFilters(prev => ({ ...prev, bankReference: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Merchant Ref</label>
                <input type="text" placeholder="Merchant reference" value={filters.payoutId} onChange={(e) => setFilters(prev => ({ ...prev, payoutId: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Amount</label>
                <input type="number" placeholder="Amount" value={filters.amount} onChange={(e) => setFilters(prev => ({ ...prev, amount: e.target.value }))} className="py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-600">Quick Filters:</span>
              <button type="button" onClick={() => handleQuickFilter('today')} className="py-2 px-4 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50">Today</button>
              <button type="button" onClick={() => handleQuickFilter('week')} className="py-2 px-4 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50">This Week</button>
              <button type="button" onClick={() => handleQuickFilter('month')} className="py-2 px-4 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50">This Month</button>
              <button type="submit" className="py-2 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700">Search</button>
              <button type="button" onClick={handleClearFilters} className="py-2 px-4 rounded-lg bg-slate-200 text-slate-700 text-sm font-semibold border-none cursor-pointer hover:bg-slate-300">Clear Filters</button>
            </div>
          </form>

          <div className="flex flex-wrap gap-2 mb-4">
            <button type="button" onClick={handleCreditMoney} className="py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-blue-700">+ CREDIT AMOUNT</button>
            <button type="button" onClick={handleAddPayout} className="py-2.5 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700">+ PAYOUT</button>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap justify-between items-center gap-4 py-4 px-4 bg-white rounded-xl border border-slate-200 shadow-sm mb-4">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-slate-200 bg-white text-slate-700 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">←</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button key={page} type="button" onClick={() => handlePageChange(page)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === page ? 'bg-violet-600 text-white border-violet-600' : 'border-2 border-slate-200 bg-white text-slate-700'}`}>{page}</button>
                ))}
                <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-slate-200 bg-white text-slate-700 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">→</button>
              </div>
              <div className="text-sm text-slate-600 font-medium">Page {currentPage} of {totalPages}</div>
            </div>
          )}

          <div className="bg-white rounded-2xl overflow-x-auto shadow-lg border border-slate-200">
            <table className="w-full min-w-[1300px] border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-br from-slate-50 to-slate-100">
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">CREATED AT</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">PAYMENT TYPE</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">AMOUNT</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">TRANSACTION FEES</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">CURR. WALL. BAL.</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">CONTACT</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">CREATED BY</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">TRANSACTION ID</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">MERCHANT REF</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">BANK REF</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">SOURCE</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">STATUS</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">RESPONSE MESSAGE</th>
                  <th className="text-slate-700 font-bold py-4 px-4 text-left border-b-2 border-slate-200 text-[10px] uppercase tracking-wider">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="text-center py-12 text-slate-500 text-sm">No transactions found. Try adjusting your search filters.</td>
                  </tr>
                ) : (
                  filteredTransactions.map((transaction, index) => (
                    <tr key={transaction.id} className={index === 0 ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-slate-50'}>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle text-xs">{transaction.createdAt}</td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle">{transaction.paymentType}</td>
                      <td className="py-4 px-4 border-b border-slate-100 align-middle">
                        <span className={`font-semibold text-sm ${transaction.paymentType === 'Payout Credit' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="py-4 px-4 border-b border-slate-100 align-middle"><span className="font-semibold text-amber-600 text-sm">{formatCurrency(transaction.transactionFees)}</span></td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle">{formatCurrency(transaction.currentWalletBalance)}</td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle text-xs">{transaction.contact}</td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle text-xs">{transaction.createdBy}</td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle text-xs">{transaction.transactionId}</td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle text-xs">{transaction.merchantReference}</td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle text-xs">{transaction.bankReference}</td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle text-xs">
                        {transaction.source === 'POOLPAY' ? 'PoolPay' : 'Viyona'}
                      </td>
                      <td className="py-4 px-4 border-b border-slate-100 align-middle">
                        {(() => {
                          const isRefundCredit = transaction.paymentType === 'Payout Credit' && /refund for failed/i.test(transaction.contact || '');
                          const isManagerCredit = transaction.paymentType === 'Payout Credit' && /credited by manager/i.test(transaction.contact || '');
                          const displayStatus = isRefundCredit ? 'Refunded' : isManagerCredit ? 'Success' : transaction.status;
                          const statusClass = displayStatus === 'Refunded'
                            ? 'bg-sky-100 text-sky-700'
                            : displayStatus === 'Success'
                              ? 'bg-emerald-100 text-emerald-700'
                              : displayStatus === 'Pending'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700';
                          return (
                            <span className={`inline-block py-1 px-3 rounded-full text-xs font-semibold uppercase ${statusClass}`}>
                              {displayStatus}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-4 px-4 border-b border-slate-100 text-slate-700 align-middle">
                        {(transaction.errorMessage || transaction.responseData) ? (
                          <button
                            type="button"
                            onClick={() => {
                              const raw = transaction.responseData;
                              if (raw != null && raw !== '') {
                                if (typeof raw === 'string') {
                                  try {
                                    setResponseMessageData(JSON.parse(raw));
                                  } catch {
                                    setResponseMessageData(raw);
                                  }
                                } else {
                                  setResponseMessageData(raw);
                                }
                              } else {
                                setResponseMessageData(transaction.errorMessage ?? null);
                              }
                              setShowResponseMessageModal(true);
                            }}
                            className="inline-flex items-center gap-1.5 py-1.5 px-2.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                            title="View response message"
                          >
                            <span className="text-base leading-none" aria-hidden>👁️</span>
                            View message
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-4 px-4 border-b border-slate-100 align-middle">
                        <div className="flex gap-2 items-center">
                          <button type="button" onClick={() => handleViewSlip(transaction)} title="View Transaction Slip" className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 cursor-pointer text-lg">👁️</button>
                          {(() => {
                            const isManagerCredit = transaction.paymentType === 'Payout Credit' && /credited by manager/i.test(transaction.contact || '');
                            return (
                              <button
                                type="button"
                                onClick={() => !isManagerCredit && handleCheckStatus(transaction)}
                                title={isManagerCredit ? 'Not available for manager credits' : 'Check live status from provider'}
                                disabled={isManagerCredit}
                                className={`inline-flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-lg border text-xs font-medium whitespace-nowrap ${isManagerCredit ? 'border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100 cursor-pointer'}`}
                              >
                                <span
                                  className="inline-block shrink-0 rounded-full"
                                  style={{ width: 10, height: 10, backgroundColor: isManagerCredit ? '#cbd5e1' : '#e53935', borderRadius: '50%' }}
                                  aria-hidden
                                  title="Live"
                                />
                                Live Status
                              </button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />

      {/* Wallet Credits Modal */}
      <WalletCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />

      {/* Response Message Modal (admin-style: full response_data) */}
      <ResponseMessageModal
        isOpen={showResponseMessageModal}
        onClose={() => setShowResponseMessageModal(false)}
        data={responseMessageData}
      />

      {/* Transaction Slip Modal */}
      {showSlipModal && selectedTransaction && (
        <TransactionSlip
          transaction={{
            id: selectedTransaction.id,
            type: 'payout',
            amount: selectedTransaction.amount,
            status: (selectedTransaction.paymentType === 'Payout Credit' && /refund for failed/i.test(selectedTransaction.contact || '')) ? 'Refunded' : selectedTransaction.status,
            date: selectedTransaction.createdAtRaw, // Use raw date for proper parsing
            recipient_name: selectedTransaction.contact,
            transaction_id: selectedTransaction.transactionId,
            bank_reference: selectedTransaction.bankReference,
            recipient_account: selectedTransaction.accountNumber,
            recipient_ifsc: selectedTransaction.ifscCode,
            merchant_reference: selectedTransaction.merchantReference,
            merchant_name: user?.name || 'Merchant',
            merchant_email: user?.email || '',
          }}
          merchantDetails={{
            name: user?.name || 'Merchant',
            email: user?.email || '',
          }}
          onClose={() => setShowSlipModal(false)}
          onDownload={handleDownloadSlip}
        />
      )}

      <AdminPayoutStatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        statusData={statusData}
        loading={statusLoading}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MerchantPayouts;
